<template>
  <!-- All device fields on one wrapping grid — no horizontal scrolling. Used as the
       editable expansion in the upload grid and as a read-only detail view on the
       submissions page. In edit mode fields mutate the (reactive) row object directly,
       same as the grid's inline cells. -->
  <div class="row g-3">
    <!-- I-REC shortcut: the Evident code/link identifies the device, so the standard
         fields relax and the blanks are filled from the public device register (in the
         form, where the seller can correct them). -->
    <div v-if="irecRelaxed && !readonly" class="col-12">
      <div class="alert alert-info small py-2 px-3 mb-0">
        <span
          v-if="lookupStatus === 'loading'"
          class="spinner-border spinner-border-sm me-2"
        />
        {{ t(irecHintKey) }}
      </div>
    </div>
    <div class="col-12 col-md-4">
      <label class="field-label" :for="fieldId('name')">{{
        t("ecolabels.form.name")
      }}</label>
      <div v-if="readonly" class="field-value">{{ device.name ?? "—" }}</div>
      <InputText
        v-else
        :id="fieldId('name')"
        v-model="fields.name"
        class="w-100 p-inputtext-sm"
        :invalid="!device.name?.trim() && !irecRelaxed"
      />
    </div>

    <div class="col-6 col-md-4">
      <label class="field-label" :for="fieldId('country')">{{
        t("ecolabels.table.country")
      }}</label>
      <div v-if="readonly" class="field-value">{{ device.country ?? "—" }}</div>
      <MultiSelectComponent
        v-else
        :modelValue="device.country ? [device.country] : []"
        :options="countryOptions ?? []"
        :placeholder="t('ecolabels.table.country')"
        size="sm"
        portal
        hideBulkActions
        :buttonId="fieldId('country')"
        @update:modelValue="
          (codes: string[]) =>
            (fields.country = codes[codes.length - 1] ?? null)
        "
      />
    </div>

    <div class="col-6 col-md-4">
      <label class="field-label" :for="fieldId('renewable_type')">{{
        t("ecolabels.table.renewable_type")
      }}</label>
      <div v-if="readonly" class="field-value">
        {{ device.renewable_type ?? "—" }}
      </div>
      <select
        v-else
        :id="fieldId('renewable_type')"
        v-model="fields.renewable_type"
        class="form-select form-select-sm"
        :class="{ 'is-invalid': !device.renewable_type && !irecRelaxed }"
      >
        <option :value="null">-</option>
        <option v-for="type in RENEWABLE_TYPES" :key="type" :value="type">
          {{ t(`ecolabels.renewable_types.${type.toLowerCase()}`) }}
        </option>
      </select>
    </div>

    <div class="col-6 col-md-4">
      <label class="field-label" :for="fieldId('registration_authority')">{{
        t("ecolabels.table.registration_authority")
      }}</label>
      <div v-if="readonly" class="field-value">
        {{ device.registration_authority ?? "—" }}
      </div>
      <select
        v-else
        :id="fieldId('registration_authority')"
        v-model="fields.registration_authority"
        class="form-select form-select-sm"
        @change="deriveIrecFields"
        :class="{
          'is-invalid':
            !!device.plant_ecolabel_id?.trim() &&
            !device.registration_authority &&
            !irecRelaxed,
        }"
      >
        <option :value="null">-</option>
        <option
          v-for="authority in REGISTRATION_AUTHORITIES"
          :key="authority"
          :value="authority"
        >
          {{ REGISTRATION_AUTHORITY_LABELS[authority] ?? authority }}
        </option>
      </select>
    </div>

    <div class="col-6 col-md-4">
      <label class="field-label" :for="fieldId('plant_ecolabel_id')">{{
        t("ecolabels.table.plant_ecolabel_id")
      }}</label>
      <div v-if="readonly" class="field-value">
        {{ device.plant_ecolabel_id ?? "—" }}
      </div>
      <InputText
        v-else
        :id="fieldId('plant_ecolabel_id')"
        v-model="fields.plant_ecolabel_id"
        class="w-100 p-inputtext-sm"
        @change="deriveIrecFields"
      />
    </div>

    <div class="col-6 col-md-4">
      <label class="field-label" :for="fieldId('technology_code')">{{
        t("ecolabels.table.technology_code")
      }}</label>
      <div v-if="readonly" class="field-value">
        {{ device.technology_code ?? "—" }}
      </div>
      <InputText
        v-else
        :id="fieldId('technology_code')"
        v-model="fields.technology_code"
        class="w-100 p-inputtext-sm"
        :placeholder="t('ecolabels.form.technology_code_placeholder')"
      />
    </div>

    <div class="col-6 col-md-4">
      <label class="field-label" :for="fieldId('capacity')">{{
        t("ecolabels.table.capacity")
      }}</label>
      <div v-if="readonly" class="field-value">
        {{
          device.capacity != null
            ? `${device.capacity} ${device.capacity_units ?? "MW"}`
            : "—"
        }}
      </div>
      <InputText
        v-else
        :id="fieldId('capacity')"
        v-model="fields.capacity"
        class="w-100 p-inputtext-sm"
        inputmode="decimal"
        placeholder="MW"
        :class="{ 'is-invalid': invalidNumbers.includes('capacity') }"
        :invalid="invalidNumbers.includes('capacity')"
      />
    </div>

    <div class="col-6 col-md-4">
      <label class="field-label" :for="fieldId('latitude')">{{
        t("ecolabels.table.latitude")
      }}</label>
      <div v-if="readonly" class="field-value">
        {{ device.latitude ?? "—" }}
      </div>
      <InputText
        v-else
        :id="fieldId('latitude')"
        v-model="fields.latitude"
        class="w-100 p-inputtext-sm"
        inputmode="decimal"
        :class="{ 'is-invalid': invalidNumbers.includes('latitude') }"
        :invalid="invalidNumbers.includes('latitude')"
      />
    </div>

    <div class="col-6 col-md-4">
      <label class="field-label" :for="fieldId('longitude')">{{
        t("ecolabels.table.longitude")
      }}</label>
      <div v-if="readonly" class="field-value">
        {{ device.longitude ?? "—" }}
      </div>
      <InputText
        v-else
        :id="fieldId('longitude')"
        v-model="fields.longitude"
        class="w-100 p-inputtext-sm"
        inputmode="decimal"
        :class="{ 'is-invalid': invalidNumbers.includes('longitude') }"
        :invalid="invalidNumbers.includes('longitude')"
      />
    </div>

    <div class="col-6 col-md-4">
      <label class="field-label" :for="fieldId('cod')">{{
        t("ecolabels.table.cod")
      }}</label>
      <div v-if="readonly" class="field-value">{{ device.cod ?? "—" }}</div>
      <InputText
        v-else
        :id="fieldId('cod')"
        v-model="fields.cod"
        class="w-100 p-inputtext-sm"
        type="date"
      />
    </div>

    <div class="col-12 col-md-8">
      <label class="field-label" :for="fieldId('address')">{{
        t("ecolabels.form.address")
      }}</label>
      <div v-if="readonly" class="field-value">{{ device.address ?? "—" }}</div>
      <InputText
        v-else
        :id="fieldId('address')"
        v-model="fields.address"
        class="w-100 p-inputtext-sm"
        :invalid="
          !device.address?.trim() &&
          !(
            isValidLatitude(device.latitude) &&
            isValidLongitude(device.longitude)
          ) &&
          !irecRelaxed
        "
      />
    </div>

    <div class="col-12 col-md-4">
      <label class="field-label" :for="fieldId('relevant_link')">{{
        t("ecolabels.table.relevant_link")
      }}</label>
      <div v-if="readonly" class="field-value text-break">
        <a
          v-if="device.relevant_link && isValidHttpUrl(device.relevant_link)"
          :href="device.relevant_link"
          target="_blank"
          rel="noopener noreferrer"
          >{{ device.relevant_link }}</a
        >
        <template v-else>{{ device.relevant_link ?? "—" }}</template>
      </div>
      <InputText
        v-else
        :id="fieldId('relevant_link')"
        v-model="fields.relevant_link"
        class="w-100 p-inputtext-sm"
        type="url"
        placeholder="https://"
        @change="deriveIrecFields"
        :invalid="
          !!device.relevant_link && !isValidHttpUrl(device.relevant_link)
        "
      />
    </div>

    <div class="col-12">
      <label class="field-label" :for="fieldId('public_notes')">{{
        t("ecolabels.table.public_notes")
      }}</label>
      <div v-if="readonly" class="field-value">
        {{ device.public_notes ?? "—" }}
      </div>
      <InputText
        v-else
        :id="fieldId('public_notes')"
        v-model="fields.public_notes"
        class="w-100 p-inputtext-sm"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import InputText from "primevue/inputtext";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";
import {
  REGISTRATION_AUTHORITIES,
  REGISTRATION_AUTHORITY_LABELS,
  RENEWABLE_TYPES,
} from "../api/ecolabelsTypes";
import {
  hasIrecIdentity,
  invalidNumberFields,
  isValidHttpUrl,
  isValidLatitude,
  isValidLongitude,
  type IrecLookupStatus,
} from "./deviceUploadMapping";
import { irecLookupHintKey, lookupAndFillIrecRow } from "./irecLookup";

/** The device fields this panel shows; structurally satisfied by both the upload grid's
 * UploadRow (edit mode) and EcolabelDevice (readonly mode). */
export interface DeviceFieldValues {
  /** Outcome of the last register lookup, when the caller tracks one (grid rows do). */
  irec_lookup?: IrecLookupStatus | null;
  name: string | null;
  country: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  renewable_type: string | null;
  technology_code: string | null;
  plant_ecolabel_id: string | null;
  registration_authority: string | null;
  capacity: number | null;
  capacity_units?: string;
  cod: string | null;
  relevant_link: string | null;
  public_notes: string | null;
}

const props = withDefaults(
  defineProps<{
    device: DeviceFieldValues;
    readonly?: boolean;
    countryOptions?: { label: string; value: string }[];
    /** Whether the pre-submission I-REC shortcut applies (relaxed required-field
     * styling plus the hint). The admin review panel turns it off: a persisted device
     * must show a missing registry as missing — nothing derives fields on update. */
    irecShortcut?: boolean;
  }>(),
  { readonly: false, countryOptions: undefined, irecShortcut: true }
);

// Deliberate two-way binding onto the caller's (reactive) row object — the panel edits
// the same row the grid shows, exactly like the grid's own inline cells; there is no
// separate draft to emit. The alias also widens the numeric trio: PrimeVue's InputText
// models strings while v-model.number writes numbers back.
type EditableFields = Omit<
  DeviceFieldValues,
  "capacity" | "latitude" | "longitude"
> &
  Record<"capacity" | "latitude" | "longitude", string | null>;
// Computed, not a one-time alias: chat turns rebuild grid rows (new objects, same
// _uid), and a reused panel instance must keep writing into the CURRENT row.
/** True when the Evident code/link identifies the device — required-field styling
 * relaxes to match the grid's blocking rules. */
const irecRelaxed = computed(
  () =>
    props.irecShortcut &&
    hasIrecIdentity({
      relevant_link: props.device.relevant_link,
      registration_authority: props.device.registration_authority,
      plant_ecolabel_id: props.device.plant_ecolabel_id,
    })
);

const fields = computed(() => props.device as unknown as EditableFields);

const invalidNumbers = computed(() => invalidNumberFields(props.device));

// Lookups started from this panel; a grid row also carries its own status (irec_lookup)
// for lookups started from the grid cells, so the two are merged for the hint.
const localLookupStatus = ref<IrecLookupStatus | null>(null);
const lookupStatus = computed(
  () => props.device.irec_lookup ?? localLookupStatus.value
);
const irecHintKey = computed(() => irecLookupHintKey(lookupStatus.value));

/** Live I-REC shortcut: derive the code/registry/link, then fill the blanks from the
 * public device register — visibly, before submitting. On change (blur/commit), not per
 * keystroke, so a half-typed code doesn't trigger a lookup. */
const deriveIrecFields = () => {
  if (!props.irecShortcut) return;
  void lookupAndFillIrecRow(fields.value, (status) => {
    localLookupStatus.value = status;
    // Written through the same row alias as every other field, so the grid's
    // expansion state and the panel hint agree.
    fields.value.irec_lookup = status;
  });
};

const { t } = useI18n();

// Unique per panel instance so several expanded rows don't collide.
const panelId = useId();
const fieldId = (field: string) => `${panelId}-${field}`;
</script>

<style scoped lang="scss">
// Quiet, scannable field grid: uppercase micro-labels over regular-weight values,
// aligned across columns in both the editable and read-only variants.
.field-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--phoenix-secondary-color);
}

.field-value {
  min-height: 1.5rem;
  font-size: 0.875rem;
  color: var(--phoenix-body-color);
  overflow-wrap: anywhere;
}
</style>
